/**
 * Vehicle (fleet) schemas.
 *
 * Trucks are a shared pool — not owned by a crew member. A vehicle is booked
 * per task (see tasks.vehicle_id) and can't be double-booked across overlapping
 * task windows. The fleet registry is supervisor-managed.
 *
 * Beyond the registry, each vehicle carries a lightweight fleet-management
 * profile: a service log (maintenance history), issue reports (problems crew
 * flag from the field), photos, and an availability status.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { VehicleIssueSeveritySchema, VehicleIssueStatusSchema, VehicleServiceCategorySchema, VehicleStatusSchema, VehicleTypeSchema, } from './enums.js';
import { AttachmentSchema } from './attachments.js';
const YearSchema = z.number().int().min(1900).max(2100);
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use yyyy-MM-dd');
export const VehicleSchema = z.object({
    id: UuidSchema,
    label: z.string().min(1).max(40).describe('e.g. "VARI-2" — unique fleet tag'),
    plate: z.string().max(20).nullable(),
    type: VehicleTypeSchema,
    status: VehicleStatusSchema,
    status_note: z.string().max(300).nullable().describe('why out of service, etc.'),
    make: z.string().max(60).nullable(),
    model: z.string().max(60).nullable(),
    year: YearSchema.nullable(),
    vin: z.string().max(40).nullable(),
    registration_expires_on: z.string().nullable().describe('yyyy-MM-dd'),
    notes: z.string().max(2000).nullable(),
    /** Open issue reports — drives the "needs attention" badge on the fleet list. */
    open_issue_count: z.number().int().min(0).default(0),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const VehicleCreateSchema = z.object({
    label: z.string().min(1).max(40),
    plate: z.string().max(20).optional().nullable(),
    type: VehicleTypeSchema.default('cargo_van'),
    make: z.string().max(60).optional().nullable(),
    model: z.string().max(60).optional().nullable(),
    year: YearSchema.optional().nullable(),
    vin: z.string().max(40).optional().nullable(),
    registration_expires_on: DateOnly.optional().nullable(),
    notes: z.string().max(2000).optional().nullable(),
});
export const VehicleUpdateSchema = z
    .object({
    label: z.string().min(1).max(40).optional(),
    plate: z.string().max(20).nullable().optional(),
    type: VehicleTypeSchema.optional(),
    status: VehicleStatusSchema.optional(),
    status_note: z.string().max(300).nullable().optional(),
    make: z.string().max(60).nullable().optional(),
    model: z.string().max(60).nullable().optional(),
    year: YearSchema.nullable().optional(),
    vin: z.string().max(40).nullable().optional(),
    registration_expires_on: DateOnly.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'Provide at least one field to update.',
});
export const VehicleListResponseSchema = z.object({ data: z.array(VehicleSchema) });
// ----------------------------------------------------------------------------
// Service log — maintenance / repair history per vehicle
// ----------------------------------------------------------------------------
export const VehicleServiceLogSchema = z.object({
    id: UuidSchema,
    vehicle_id: UuidSchema,
    performed_on: z.string().describe('yyyy-MM-dd'),
    category: VehicleServiceCategorySchema,
    vendor: z.string().max(200).nullable().describe('"In-house" or the shop name'),
    cost_cents: z.number().int().min(0).nullable(),
    odometer: z.number().int().min(0).nullable(),
    notes: z.string().max(2000).nullable(),
    created_by: UuidSchema,
    created_by_name: z.string().nullable(),
    photos: z.array(AttachmentSchema).default([]),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const VehicleServiceLogCreateSchema = z.object({
    performed_on: DateOnly,
    category: VehicleServiceCategorySchema,
    vendor: z.string().max(200).optional().nullable(),
    cost_cents: z.number().int().min(0).optional().nullable(),
    odometer: z.number().int().min(0).optional().nullable(),
    notes: z.string().max(2000).optional().nullable(),
});
export const VehicleServiceLogUpdateSchema = z
    .object({
    performed_on: DateOnly.optional(),
    category: VehicleServiceCategorySchema.optional(),
    vendor: z.string().max(200).nullable().optional(),
    cost_cents: z.number().int().min(0).nullable().optional(),
    odometer: z.number().int().min(0).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'Provide at least one field to update.',
});
export const VehicleServiceLogListResponseSchema = z.object({
    data: z.array(VehicleServiceLogSchema),
});
// ----------------------------------------------------------------------------
// Issue reports — problems crew flag from the field
// ----------------------------------------------------------------------------
export const VehicleIssueSchema = z.object({
    id: UuidSchema,
    vehicle_id: UuidSchema,
    vehicle_label: z.string().nullable().describe('joined — for the fleet-wide open list'),
    reported_by: UuidSchema,
    reported_by_name: z.string().nullable(),
    task_id: UuidSchema.nullable().describe('the job they were on when reporting'),
    title: z.string(),
    description: z.string().nullable(),
    severity: VehicleIssueSeveritySchema,
    status: VehicleIssueStatusSchema,
    resolved_by: UuidSchema.nullable(),
    resolved_by_name: z.string().nullable(),
    resolved_at: IsoDateTimeSchema.nullable(),
    resolution_note: z.string().nullable(),
    photos: z.array(AttachmentSchema).default([]),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const VehicleIssueCreateSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional().nullable(),
    severity: VehicleIssueSeveritySchema.default('normal'),
    task_id: UuidSchema.optional().nullable(),
});
export const VehicleIssueUpdateSchema = z
    .object({
    status: VehicleIssueStatusSchema.optional(),
    severity: VehicleIssueSeveritySchema.optional(),
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    resolution_note: z.string().max(2000).nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'Provide at least one field to update.',
});
export const VehicleIssueListResponseSchema = z.object({
    data: z.array(VehicleIssueSchema),
});
//# sourceMappingURL=vehicles.js.map